import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EuiServiceStatus, EuiUserRight } from '@eui/base';
import {
    CONFIG_TOKEN,
    EuiAppConfig,
    EuiPermissionService,
    I18nService,
    ModuleConfig,
    UserDetails,
    UserPreferences,
    UserService,
    UserState,
} from '@eui/core';
import { Observable, of, ReplaySubject, tap, zip } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';

@Injectable({
    providedIn: 'root',
})
export class AppStarterService {
    protected userService = inject(UserService);
    private appConfigService = inject(AppConfigService);
    protected euiPermissionService = inject(EuiPermissionService);
    protected i18nService = inject(I18nService);
    private config = inject<EuiAppConfig>(CONFIG_TOKEN);
    protected http = inject(HttpClient);

    defaultUserPreferences: UserPreferences;
    start$: Observable<[EuiServiceStatus, EuiServiceStatus, EuiServiceStatus]>;
    private startSubject = new ReplaySubject<
        [EuiServiceStatus, EuiServiceStatus, EuiServiceStatus]
    >(1);

    start(): Observable<[EuiServiceStatus, EuiServiceStatus, EuiServiceStatus]> {
        if (!this.start$) {
            this.start$ = this.initializeStartupData().pipe(
                tap((data) => this.startSubject.next(data))
            );
        }
        return this.start$;
    }

    getStartupData() {
        return zip([
            this.appConfigService.getConfig(),
            this.appConfigService.getConfiguration(),
            this.userService.getState(),
        ]);
    }

    /**
     * Fetches user details,
     * create user: UserState object
     * then initialise to the UserService on run time
     */
    initUserService(): Observable<EuiServiceStatus> {
        return this.fetchUserDetails().pipe(
            switchMap((userDetails) => {
                const user: UserState = {
                    ...userDetails,
                    fullName: userDetails.firstName + ' ' + userDetails.lastName,
                    rights: [],
                    preferences: {
                        ...this.defaultUserPreferences,
                    },
                };
                return this.userService.init(user);
            })
        );
    }

    /**
     * Fetches user details&preferences then merges with the default prefs,
     * create user: UserState object
     * then initialise to the UserService on run time
     */
    initPermissionService(): Observable<EuiServiceStatus> {
        return this.fetchUserRights().pipe(
            switchMap((userRights) => this.euiPermissionService.init(userRights || []))
        );
    }

    private initializeStartupData(): Observable<
        [EuiServiceStatus, EuiServiceStatus, EuiServiceStatus]
    > {
        return this.buildUserState().pipe(
            switchMap((userState) =>
                // 1. Force UserService to initialize first
                this.userService.init(userState).pipe(
                    switchMap((userServiceStatus) =>
                        // 2. ONLY after UserService finishes, initialize permission
                        zip([
                            of(userServiceStatus),
                            this.euiPermissionService.init(userState.rights),
                            this.i18nService.init({
                                activeLang: this.getStoredLanguage(),
                            }),
                        ])
                    )
                )
            ),
        );
    }

    private buildUserState(): Observable<UserState> {
        return zip(this.fetchUserDetails(), this.fetchUserRights()).pipe(
            map(([userDetails, userRights]) => {
                const userState: UserState = {
                    ...userDetails,
                    fullName:
                        `${userDetails?.firstName ?? ''} ${userDetails?.lastName ?? ''}`.trim(),
                    rights: userRights || [],
                    preferences: {
                        ...this.defaultUserPreferences,
                    },
                };
                return userState;
            })
        );
    }

    /**
     * Retrieves the stored language from localStorage, falls back to default 'en'
     */
    private getStoredLanguage(): string {
        try {
            const storedState = localStorage.getItem('state');
            const storedI18n = localStorage.getItem('i18n');
            if (storedI18n) {
                const parsedI18n = JSON.parse(storedI18n);
                const language = parsedI18n?.activeLang;
                return language;
            }
            if (storedState) {
                const parsedState = JSON.parse(storedState);
                const language = parsedState?.user?.preferences?.lang || 'en';
                return language;
            }
        } catch (error) {
            console.error('Error retrieving language from localStorage:', error);
        }
        return 'en';
    }

    /**
     * Fetches user details
     */

    private fetchUserDetails(): Observable<UserDetails> {
        // const url = this.config.modules.your_custom_module.your_custom_endpoint
        const url = new LegacyUserServiceHelper(
            this.config.modules as LegacyModulesConfig
        ).getEndPoint('details');
        const user = { userId: 'anonymous' };

        if (!url) {
            return of(user);
        }
        return this.http.get<UserDetails>(url);
    }

    /**
     * Fetches user rights
     */
    private fetchUserRights(): Observable<EuiUserRight[]> {
        // const url = this.config.modules.your_custom_module.your_custom_endpoint
        const url = new LegacyUserServiceHelper(
            this.config.modules as LegacyModulesConfig
        ).getEndPoint('rights');

        if (!url) {
            return of([]);
        }
        return this.http.get<EuiUserRight[]>(url);
    }
}

export class LegacyUserServiceHelper {
    legacyApiConfig: LegacyUserApiConfig;

    constructor(private modulesConfig: LegacyModulesConfig) {
        this.legacyApiConfig = this.modulesConfig.core && this.modulesConfig.core.api;
    }

    public getEndPoint(target = 'details'): string {
        if (
            !this.legacyApiConfig ||
            !this.legacyApiConfig.user ||
            !this.legacyApiConfig.user[target]
        ) {
            return;
        }

        const base = this.legacyApiConfig.base || '';
        const baseUser = this.legacyApiConfig.user.base || '';

        if (this.legacyApiConfig.user[target].indexOf('http') > -1) {
            return this.legacyApiConfig.user[target];
        } else {
            return `${base + baseUser + this.legacyApiConfig.user[target]}`;
        }
    }
}

/**
 * Legacy Modules configuration
 */
export interface LegacyModulesConfig {
    core?: LegacyCoreModuleConfig;

    [module: string]: ModuleConfig;
}

/** Removed Core configuration */
export interface LegacyCoreModuleConfig {
    api?: LegacyUserApiConfig;

    [key: string]: unknown;
}

/** Removed User API configuration */
export interface LegacyUserApiConfig {
    /** general prefix for uri (user uri, etc) */
    base?: string;
    user?: {
        /** prefix for all other user uri (details, preferences, etc) */
        base?: string;
        /** uri for user details (detail was deprecated at 7'th version) */
        details?: string;
        /** uri for user preferences */
        preferences?: string;
        target?: string;
    };
}
